import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  asapScheduler,
  debounceTime,
  fromEvent,
  Observable,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';

@Component({
  selector: 'app-text-area',
  templateUrl: 'text-area.component.html',
  styleUrls: ['text-area.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: TextAreaComponent,
    },
  ],
})
export class TextAreaComponent
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('inputEl', { static: true })
  inputElRef: ElementRef<HTMLTextAreaElement>;

  private formEl: HTMLFormElement;
  private formResetSubscription: Subscription;

  @Input()
  rows = 2;
  @Input()
  resize: 'none' | 'vertical' | 'horizontal' | 'both' = 'both';

  @Input()
  autocomplete = true;

  @Input()
  value: string;
  @Output()
  valueChange = new EventEmitter<string>();

  @Output()
  onReset = new EventEmitter<any>();
  @Output()
  onInput = new EventEmitter<any>();
  @Output()
  onFocus = new EventEmitter<any>();

  @Output()
  onKeydown = new EventEmitter<any>();

  private onChange = (value) => {};

  private onTouched = () => {};

  @Input()
  name: string;

  @Input()
  placeholder: string;

  @Input()
  readOnly: boolean;
  @Input()
  disabled: boolean;

  @Input()
  maxLength: number;

  @Input()
  showErrorEnabled: boolean = true;
  @Input()
  showClearButton: boolean = true;

  @Input()
  label: string;

  focused = false;
  touched = false;

  get empty() {
    return !this.inputElRef?.nativeElement?.value && !this._isBadInput();
  }

  private lifecycle = new Subject<void>();

  ngOnInit(): void {
    let parentEl = this.inputElRef.nativeElement.form;

    if (parentEl instanceof HTMLFormElement) {
      this.formEl = parentEl;
      fromEvent(this.formEl, 'reset')
        .pipe(debounceTime(0, asapScheduler), takeUntil(this.lifecycle))
        .subscribe(() => {
          this.onInputChange();

          this.onReset.emit();
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.maxLength) {
      if (typeof this.maxLength === 'number') {
        this.inputElRef.nativeElement.setAttribute(
          'maxLength',
          this.maxLength as any
        );
      } else {
        this.inputElRef.nativeElement.removeAttribute('maxLength');
      }
    }

    if (changes.value) {
      this._setInputValue();
    }
  }

  ngAfterViewInit(): void {
    this._setInputValue();
  }

  ngOnDestroy() {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  writeValue(value: any): void {
    if (this.value !== value) {
      this.value = value;
      this._setInputValue();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onFocusChanged(focus) {
    this.focused = focus;

    this.onFocus.emit(focus);
  }

  onInputChange() {
    this.formResetSubscription?.unsubscribe();

    const inputEl = this.inputElRef.nativeElement;
    const value = inputEl.value;
    if (this.value !== value) {
      this.value = value;

      this.valueChange.emit(value);
      this.onChange?.(value);
    }
  }

  onInputValue(event) {
    this.onInputChange();

    this.onInput.emit({ event, component: this });
  }

  onInputKeydown(event) {
    this.onKeydown.emit({ event, component: this });
  }

  @HostListener('click')
  onContainerClick() {
    if (!this.focused && !this.disabled && !this.readOnly) {
      this.focused = true;
      this.inputElRef.nativeElement.focus();

      if (!this.touched) {
        this.touched = true;
        this.onTouched?.();
      }
    }
  }

  protected _isBadInput() {
    // The `validity` property won't be present on platform-server.
    let validity = (this.inputElRef?.nativeElement as HTMLTextAreaElement)
      ?.validity;
    return validity && validity.badInput;
  }

  private _setInputValue() {
    if (this.inputElRef?.nativeElement) {
      this.inputElRef.nativeElement.value = this.value ?? '';
    }
  }
}
